/**
 * NeoCoreCreditosPage — Dashboard BI de la cartera de créditos.
 *
 * Pensado para que el operador / gerente entienda de un vistazo:
 *   1. HERO — Cartera vigente, mora, % vencida, ingresos cobrados YTD
 *   2. RIESGO — PAR (Portfolio at Risk) 1+, 8+, 30+ días, distribución por
 *      categoría INGECOP (A/B/C/D/E) con barras
 *   3. INGRESOS — intereses cobrados últimos 30 días + mes actual vs anterior
 *   4. ACTIVIDAD — desembolsos vs cobros últimos 30 días (sparkline simple)
 *   5. PIPELINE — solicitudes pendientes / aprobadas / por desembolsar
 *   6. CARTERA POR PRODUCTO — distribución por tipo_credito
 *   7. TABLA — los últimos 100 créditos con filtro + búsqueda
 *
 * Los datos vienen de credito + credito_cuota + credito_pago + solicitud_credito
 * en un fetch único. Se calculan client-side las métricas porque el volumen
 * es chico (MVP). Cuando crezca, mover a una vista o RPC dedicada.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowsClockwise,
  Coins,
  HandCoins,
  MagnifyingGlass,
  Receipt,
  TrendUp,
  Warning,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useRiesgoCartera } from '@/components/RiesgoCartera'

// =============================================================
// Tipos
// =============================================================

type CreditoRow = {
  id: string
  tipo_nombre: string
  asociado_id: string
  asociado_nombre: string
  asociado_no: string
  capital_inicial: number
  saldo_capital: number
  intereses_cobrados: number
  estado: string
  estado_provision: string | null
  fecha_desembolso: string
  fecha_ultima_cuota: string
  total_cuotas: number
  proxima_cuota_monto: number
  proxima_cuota_fecha: string | null
  dias_atraso: number
  cuotas_pendientes: number
}

type SolicitudRow = {
  estado: string
}

type PagoRow = {
  fecha_pago: string
  aplicado_interes: number
  aplicado_capital: number
  aplicado_mora: number
}

type Filtro = 'todos' | 'vigentes' | 'mora' | 'cancelados' | 'castigados'

// =============================================================
// Helpers
// =============================================================

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtQCompact = (n: number) => {
  if (n >= 1_000_000) return 'Q ' + (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 10_000) return 'Q ' + Math.round(n / 1000) + 'K'
  return fmtQ(n)
}

const fmtFecha = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

const ESTADO_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  vigente:               { label: 'Vigente',       color: 'var(--ok-ink, #0f8a57)',   bg: 'rgba(15,138,87,0.08)' },
  al_dia:                { label: 'Al día',        color: 'var(--ok-ink, #0f8a57)',   bg: 'rgba(15,138,87,0.08)' },
  en_mora:               { label: 'En mora',       color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  cancelado:             { label: 'Cancelado',     color: 'var(--ink-soft, #4b5563)',bg: 'rgba(75,85,99,0.08)' },
  cancelado_anticipado:  { label: 'Pre-cancelado', color: 'var(--ink-soft, #4b5563)',bg: 'rgba(75,85,99,0.08)' },
  castigado:             { label: 'Castigado',     color: 'var(--danger, #a32d2d)',  bg: 'rgba(163,45,45,0.08)' },
}

// =============================================================
// Página
// =============================================================

export function NeoCoreCreditosPage() {
  const riesgo = useRiesgoCartera()  // mora = 31+ días calendario (0392)
  const [rows, setRows] = useState<CreditoRow[]>([])
  const [solicitudes, setSolicitudes] = useState<SolicitudRow[]>([])
  const [pagos, setPagos] = useState<PagoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [search, setSearch] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [creditosResp, solicitudesResp, pagosResp] = await Promise.all([
        supabase.from('credito').select(`
          id, capital_inicial, saldo_capital, intereses_cobrados, estado, estado_provision,
          fecha_desembolso, fecha_ultima_cuota, total_cuotas,
          asociado:asociado_id (id, no_asociado, nombre_completo),
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado, dias_atraso_actual
          )
        `).order('fecha_desembolso', { ascending: false }).limit(200),
        supabase.from('solicitud_credito').select('estado').in('estado', ['pendiente', 'aprobada']),
        supabase.from('credito_pago').select('fecha_pago, aplicado_interes, aplicado_capital, aplicado_mora')
          .gte('fecha_pago', new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()),
      ])
      if (creditosResp.error) throw creditosResp.error

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const mapped: CreditoRow[] = (creditosResp.data ?? []).map((r) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c = r as any
        const cuotasPend = (c.cuotas ?? [])
          .filter((q: { estado: string }) => q.estado !== 'pagada' && q.estado !== 'castigada')
          .sort((a: { numero: number }, b: { numero: number }) => a.numero - b.numero)
        const prox = cuotasPend[0]
        const proxMonto = prox
          ? (Number(prox.capital) - Number(prox.capital_pagado)) +
            (Number(prox.interes) - Number(prox.interes_pagado)) +
            (Number(prox.mora_acumulada) - Number(prox.mora_pagada))
          : 0
        const maxAtraso = cuotasPend.reduce(
          (m: number, q: { dias_atraso_actual: number }) => Math.max(m, Number(q.dias_atraso_actual) || 0),
          0,
        )
        return {
          id: c.id,
          tipo_nombre: c.tipo?.nombre ?? 'Crédito',
          asociado_id: c.asociado?.id ?? '',
          asociado_nombre: c.asociado?.nombre_completo ?? '—',
          asociado_no: c.asociado?.no_asociado ?? '—',
          capital_inicial: Number(c.capital_inicial),
          saldo_capital: Number(c.saldo_capital),
          intereses_cobrados: Number(c.intereses_cobrados ?? 0),
          estado: c.estado,
          estado_provision: c.estado_provision,
          fecha_desembolso: c.fecha_desembolso,
          fecha_ultima_cuota: c.fecha_ultima_cuota,
          total_cuotas: c.total_cuotas,
          proxima_cuota_monto: proxMonto,
          proxima_cuota_fecha: prox?.fecha_vencimiento ?? null,
          dias_atraso: maxAtraso,
          cuotas_pendientes: cuotasPend.length,
        }
      })
      setRows(mapped)
      setSolicitudes((solicitudesResp.data ?? []) as SolicitudRow[])
      setPagos((pagosResp.data ?? []) as PagoRow[])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  // ====================== KPIs y métricas ======================

  const metricas = useMemo(() => {
    const vigentes = rows.filter((r) => ['vigente', 'al_dia', 'en_mora'].includes(r.estado))
    const enMora = vigentes.filter((r) => r.dias_atraso > 0)
    const castigados = rows.filter((r) => r.estado === 'castigado')

    const totalCartera = vigentes.reduce((s, r) => s + r.saldo_capital, 0)
    const totalMora = enMora.reduce((s, r) => s + r.saldo_capital, 0)
    const totalDesembolsadoVigente = vigentes.reduce((s, r) => s + r.capital_inicial, 0)
    const pctVencida = totalCartera > 0 ? (totalMora / totalCartera) * 100 : 0
    const interesesCobradosTotal = rows.reduce((s, r) => s + r.intereses_cobrados, 0)

    // PAR (Portfolio at Risk) — por bandas de días
    const par = {
      par1:  vigentes.filter((r) => r.dias_atraso >= 1).reduce((s, r) => s + r.saldo_capital, 0),
      par8:  vigentes.filter((r) => r.dias_atraso >= 8).reduce((s, r) => s + r.saldo_capital, 0),
      par16: vigentes.filter((r) => r.dias_atraso >= 16).reduce((s, r) => s + r.saldo_capital, 0),
      par30: vigentes.filter((r) => r.dias_atraso >= 30).reduce((s, r) => s + r.saldo_capital, 0),
    }
    const parPct = {
      par1:  totalCartera > 0 ? (par.par1  / totalCartera) * 100 : 0,
      par8:  totalCartera > 0 ? (par.par8  / totalCartera) * 100 : 0,
      par16: totalCartera > 0 ? (par.par16 / totalCartera) * 100 : 0,
      par30: totalCartera > 0 ? (par.par30 / totalCartera) * 100 : 0,
    }

    // Distribución por categoría de provisión
    const distCategoria: Record<string, { count: number; saldo: number }> = {}
    for (const r of vigentes) {
      const cat = r.estado_provision ?? '?'
      const cur = distCategoria[cat] ?? { count: 0, saldo: 0 }
      cur.count += 1
      cur.saldo += r.saldo_capital
      distCategoria[cat] = cur
    }

    // Distribución por producto
    const distProducto: Record<string, { count: number; saldo: number }> = {}
    for (const r of vigentes) {
      const cur = distProducto[r.tipo_nombre] ?? { count: 0, saldo: 0 }
      cur.count += 1
      cur.saldo += r.saldo_capital
      distProducto[r.tipo_nombre] = cur
    }

    // Ingresos del mes vs mes pasado (de credito_pago — más fino que intereses_cobrados acumulado)
    const now = new Date()
    const startMes = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
    const startMesPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime()
    let ingresosMes = 0, ingresosMesPrev = 0
    let interesesMes = 0, moraMes = 0, capitalMes = 0
    for (const p of pagos) {
      const t = new Date(p.fecha_pago).getTime()
      const intereses = Number(p.aplicado_interes) || 0
      const mora = Number(p.aplicado_mora) || 0
      const cap = Number(p.aplicado_capital) || 0
      if (t >= startMes) {
        ingresosMes += intereses + mora
        interesesMes += intereses
        moraMes += mora
        capitalMes += cap
      } else if (t >= startMesPrev) {
        ingresosMesPrev += intereses + mora
      }
    }
    const ingresosDelta = ingresosMesPrev > 0 ? ((ingresosMes - ingresosMesPrev) / ingresosMesPrev) * 100 : null

    // Pipeline (solicitudes)
    const pendientes = solicitudes.filter((s) => s.estado === 'pendiente').length
    const aprobadas = solicitudes.filter((s) => s.estado === 'aprobada').length

    // Desembolsos últimos 30 días
    const last30 = Date.now() - 30 * 24 * 60 * 60 * 1000
    const desembolsos30 = rows
      .filter((r) => new Date(r.fecha_desembolso + 'T12:00:00').getTime() >= last30)
    const montoDesembolsado30 = desembolsos30.reduce((s, r) => s + r.capital_inicial, 0)

    return {
      totalCartera, totalMora, totalDesembolsadoVigente, pctVencida, interesesCobradosTotal,
      par, parPct,
      distCategoria, distProducto,
      ingresosMes, ingresosMesPrev, ingresosDelta,
      interesesMes, moraMes, capitalMes,
      pendientes, aprobadas,
      countVigentes: vigentes.length,
      countMora: enMora.length,
      countCancelados: rows.filter((r) => ['cancelado', 'cancelado_anticipado'].includes(r.estado)).length,
      countCastigados: castigados.length,
      countTotal: rows.length,
      desembolsos30: desembolsos30.length,
      montoDesembolsado30,
    }
  }, [rows, solicitudes, pagos])

  // Filtro tabla
  const filtered = useMemo(() => {
    let list = rows
    if (filtro === 'vigentes') list = list.filter((r) => ['vigente', 'al_dia'].includes(r.estado) && r.dias_atraso === 0)
    else if (filtro === 'mora') list = list.filter((r) => r.dias_atraso > 0 || r.estado === 'en_mora')
    else if (filtro === 'cancelados') list = list.filter((r) => ['cancelado', 'cancelado_anticipado'].includes(r.estado))
    else if (filtro === 'castigados') list = list.filter((r) => r.estado === 'castigado')

    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) =>
        r.asociado_nombre.toLowerCase().includes(q) ||
        r.asociado_no.toLowerCase().includes(q) ||
        r.id.toLowerCase().startsWith(q),
      )
    }
    return list
  }, [rows, filtro, search])

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
        Cargando cartera…
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>
        <Warning size={28} weight="duotone" /> <br/>
        {error}
      </div>
    )
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.015em' }}>
            Cartera de créditos
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Estado general, riesgo, ingresos y actividad reciente.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="btn btn-ghost"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ArrowsClockwise size={13} weight="bold" />
          Refrescar
        </button>
      </div>

      {/* ============================================================
          HERO METRICS — 4 cards principales
          ============================================================ */}
      <div style={{
        display: 'grid', gap: 12, marginBottom: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      }}>
        <KpiCard
          eyebrow="Cartera vigente"
          monto={fmtQ(metricas.totalCartera)}
          detalle={`${metricas.countVigentes} crédito${metricas.countVigentes === 1 ? '' : 's'} activos`}
          Icon={HandCoins}
          tono="brand"
        />
        <KpiCard
          eyebrow="Mora 31+ días"
          monto={(riesgo.mora?.pct ?? 0).toFixed(1) + '%'}
          detalle={`${fmtQ(riesgo.mora?.saldo ?? 0)} en ${riesgo.mora?.creditos ?? 0} créditos · pre-mora ${(riesgo.get('pre_mora')?.pct ?? 0).toFixed(1)}%`}
          Icon={Warning}
          tono={(riesgo.mora?.pct ?? 0) > 10 ? 'danger' : (riesgo.mora?.pct ?? 0) > 5 ? 'warning' : 'ok'}
        />
        <KpiCard
          eyebrow="Ingresos del mes"
          monto={fmtQ(metricas.ingresosMes)}
          detalle={
            metricas.ingresosDelta === null
              ? 'Sin comparativa'
              : `${metricas.ingresosDelta >= 0 ? '+' : ''}${metricas.ingresosDelta.toFixed(1)}% vs mes anterior`
          }
          Icon={TrendUp}
          tono={metricas.ingresosDelta === null
            ? 'neutral'
            : metricas.ingresosDelta >= 0 ? 'ok' : 'warning'}
        />
        <KpiCard
          eyebrow="Desembolsado · 30 días"
          monto={fmtQCompact(metricas.montoDesembolsado30)}
          detalle={`${metricas.desembolsos30} nuevos créditos`}
          Icon={Coins}
          tono="neutral"
        />
      </div>

      {/* ============================================================
          ROW 2 — Riesgo (PAR) + Ingresos del mes desglose
          ============================================================ */}
      <div style={{
        display: 'grid', gap: 12, marginBottom: 14,
        gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
      }}>
        {/* Risk panel · PAR */}
        <Panel title="Portfolio at Risk (PAR)" subtitle="Saldo en riesgo según días de atraso">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ParBar label="PAR 1+ días"  pct={metricas.parPct.par1}  monto={metricas.par.par1}  tono="warning" />
            <ParBar label="PAR 8+ días"  pct={metricas.parPct.par8}  monto={metricas.par.par8}  tono="warning" />
            <ParBar label="PAR 16+ días" pct={metricas.parPct.par16} monto={metricas.par.par16} tono="danger" />
            <ParBar label="PAR 30+ días" pct={metricas.parPct.par30} monto={metricas.par.par30} tono="danger" />
          </div>
          <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
            <strong>Cómo leerlo:</strong> el PAR es el saldo de créditos con N o más días de atraso,
            como % de la cartera vigente. Es el indicador estándar de salud cooperativa.
          </p>
        </Panel>

        {/* Ingresos del mes desglose */}
        <Panel title="Ingresos del mes" subtitle="Desglose de lo cobrado por concepto">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Stat label="Intereses contractuales" monto={metricas.interesesMes} tono="brand" />
            <Stat label="Intereses moratorios" monto={metricas.moraMes} tono="warning" />
            <Stat label="Recuperación de capital" monto={metricas.capitalMes} tono="ok" />
            <div style={{
              paddingTop: 8, borderTop: '1px solid var(--hairline)',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Total cobrado</span>
              <span style={{ fontSize: 14, fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
                {fmtQ(metricas.interesesMes + metricas.moraMes + metricas.capitalMes)}
              </span>
            </div>
          </div>
        </Panel>
      </div>

      {/* ============================================================
          ROW 3 — Categoría provisión + Distribución por producto
          ============================================================ */}
      <div style={{
        display: 'grid', gap: 12, marginBottom: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      }}>
        {/* Distribución por categoría de provisión (INGECOP) */}
        <Panel title="Por categoría de provisión" subtitle="Distribución del saldo según escala INGECOP">
          <CategoriaDistribucion dist={metricas.distCategoria} total={metricas.totalCartera} />
        </Panel>

        {/* Distribución por producto */}
        <Panel title="Por producto" subtitle="Cartera vigente por tipo de crédito">
          <ProductoDistribucion dist={metricas.distProducto} total={metricas.totalCartera} />
        </Panel>

        {/* Pipeline */}
        <Panel title="Pipeline de solicitudes" subtitle="Originación en curso">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <PipeRow label="Pendientes de aprobación" count={metricas.pendientes} tono="warning" />
            <PipeRow label="Aprobadas · sin desembolsar" count={metricas.aprobadas} tono="brand" />
          </div>
          {metricas.pendientes + metricas.aprobadas === 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>
              Sin pipeline activo. Las solicitudes nuevas aparecerán acá.
            </p>
          )}
          <Link
            to="/neocore/creditos/originacion"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              marginTop: 12, padding: '8px 12px',
              background: '#F04E23', color: 'white',
              borderRadius: 8, fontSize: 12, fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Ir a Originación →
          </Link>
        </Panel>
      </div>

      {/* ============================================================
          TABLA — Listado filtrable
          ============================================================ */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            ['todos', 'Todos', metricas.countTotal],
            ['vigentes', 'Vigentes', metricas.countVigentes - metricas.countMora],
            ['mora', 'En mora', metricas.countMora],
            ['cancelados', 'Cancelados', metricas.countCancelados],
            ['castigados', 'Castigados', metricas.countCastigados],
          ] as Array<[Filtro, string, number]>).map(([key, label, n]) => {
            const active = filtro === key
            return (
              <button
                key={key}
                onClick={() => setFiltro(key)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 99,
                  background: active ? 'var(--brand-50)' : 'transparent',
                  color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid', borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
                }}
              >
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)',
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  fontFeatureSettings: '"tnum"',
                }}>{n}</span>
              </button>
            )
          })}
        </div>

        <div style={{ flex: 1 }} />

        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)',
          minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Asociado, n° o ID del crédito…"
            style={{
              flex: 1, border: 0, background: 'transparent',
              outline: 'none', fontSize: 13, fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            No hay créditos que coincidan con los filtros.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <Th>Asociado</Th>
                <Th>Producto</Th>
                <Th align="right">Capital</Th>
                <Th align="right">Saldo</Th>
                <Th align="right">Próx. cuota</Th>
                <Th>Vence</Th>
                <Th>Mora</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => <CreditoRowComp key={r.id} r={r} />)}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'center', marginTop: 12 }}>
        Se muestran los últimos 200 créditos · orden desembolso descendente.
      </p>
    </div>
  )
}

// =============================================================
// Sub-componentes
// =============================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function KpiCard({ eyebrow, monto, detalle, Icon, tono }: {
  eyebrow: string; monto: string; detalle: string; Icon: any
  tono: 'brand' | 'warning' | 'danger' | 'ok' | 'neutral'
}) {
  const tonos = {
    brand:   { color: 'var(--brand-700)', bg: 'var(--brand-50)' },
    warning: { color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
    danger:  { color: 'var(--danger, #a32d2d)', bg: 'rgba(163,45,45,0.10)' },
    ok:      { color: 'var(--ok-ink, #0f8a57)', bg: 'rgba(15,138,87,0.10)' },
    neutral: { color: 'var(--ink-soft)', bg: 'var(--n-100)' },
  }[tono]
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--hairline)',
      borderRadius: 14, padding: '14px 16px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: tonos.bg, color: tonos.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} weight="duotone" />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
          color: 'var(--muted-foreground)', textTransform: 'uppercase',
        }}>{eyebrow}</p>
        <p style={{
          margin: '4px 0 0', fontSize: 22, fontWeight: 800,
          color: 'var(--ink)', fontFeatureSettings: '"tnum"', letterSpacing: '-0.01em',
        }}>{monto}</p>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-soft)' }}>{detalle}</p>
      </div>
    </div>
  )
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--hairline)',
      borderRadius: 14, padding: '16px 18px',
    }}>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{
          margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
          color: 'var(--brand-700)', textTransform: 'uppercase',
        }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  )
}

function ParBar({ label, pct, monto, tono }: { label: string; pct: number; monto: number; tono: 'warning' | 'danger' }) {
  const color = tono === 'danger' ? 'var(--danger, #a32d2d)' : 'var(--warning, #d97706)'
  const bg = tono === 'danger' ? 'rgba(163,45,45,0.10)' : 'rgba(217,119,6,0.10)'
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 800, fontFeatureSettings: '"tnum"', color: 'var(--ink)' }}>
          {pct.toFixed(2)}%
          <span style={{ fontSize: 10, color: 'var(--muted-foreground)', marginLeft: 6, fontWeight: 600 }}>
            {fmtQ(monto)}
          </span>
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: bg, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 99,
          background: color, transition: 'width 0.4s ease-out',
        }} />
      </div>
    </div>
  )
}

function Stat({ label, monto, tono }: { label: string; monto: number; tono: 'brand' | 'warning' | 'ok' }) {
  const color = {
    brand: 'var(--brand-700)',
    warning: 'var(--warning, #d97706)',
    ok: 'var(--ok-ink, #0f8a57)',
  }[tono]
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{label}</span>
      <span style={{
        fontSize: 14, fontWeight: 800, fontFeatureSettings: '"tnum"', color,
      }}>
        {fmtQ(monto)}
      </span>
    </div>
  )
}

function CategoriaDistribucion({
  dist, total,
}: {
  dist: Record<string, { count: number; saldo: number }>
  total: number
}) {
  const orden = ['A', 'B', 'C', 'D', 'E']
  const colores: Record<string, { color: string; bg: string }> = {
    A: { color: 'var(--ok-ink, #0f8a57)', bg: 'rgba(15,138,87,0.10)' },
    B: { color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
    C: { color: '#c2410c', bg: 'rgba(194,65,12,0.10)' },
    D: { color: 'var(--danger, #a32d2d)', bg: 'rgba(163,45,45,0.10)' },
    E: { color: '#7f1d1d', bg: 'rgba(127,29,29,0.12)' },
  }
  const cats = orden.filter((c) => dist[c])
  if (cats.length === 0) {
    return <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>Sin cartera vigente.</p>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Barra apilada */}
      <div style={{ height: 10, borderRadius: 99, background: 'var(--n-100)', overflow: 'hidden', display: 'flex' }}>
        {cats.map((c) => {
          const pct = (dist[c].saldo / total) * 100
          return (
            <div key={c} style={{
              width: `${pct}%`, height: '100%',
              background: colores[c]?.color ?? 'var(--ink-soft)',
            }} />
          )
        })}
      </div>
      {/* Leyenda */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {cats.map((c) => {
          const pct = (dist[c].saldo / total) * 100
          const col = colores[c]?.color ?? 'var(--ink-soft)'
          return (
            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: col, flexShrink: 0 }} />
              <span style={{ width: 18, fontWeight: 700, color: col }}>{c}</span>
              <span style={{ color: 'var(--ink-soft)', flex: 1 }}>
                {dist[c].count} crédito{dist[c].count === 1 ? '' : 's'}
              </span>
              <span style={{ fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                {pct.toFixed(1)}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ProductoDistribucion({
  dist, total,
}: {
  dist: Record<string, { count: number; saldo: number }>
  total: number
}) {
  const items = Object.entries(dist).sort(([, a], [, b]) => b.saldo - a.saldo)
  if (items.length === 0) {
    return <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>Sin cartera vigente.</p>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map(([nombre, info]) => {
        const pct = (info.saldo / total) * 100
        return (
          <div key={nombre}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {nombre}
                <span style={{ fontSize: 10, color: 'var(--muted-foreground)', marginLeft: 6 }}>
                  · {info.count} créd.
                </span>
              </span>
              <span style={{ fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
                {fmtQ(info.saldo)}
              </span>
            </div>
            <div style={{ height: 5, borderRadius: 99, background: 'var(--n-100)', overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`, height: '100%', borderRadius: 99,
                background: 'linear-gradient(90deg, var(--brand-500), var(--brand-700))',
              }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PipeRow({ label, count, tono }: { label: string; count: number; tono: 'brand' | 'warning' }) {
  const color = tono === 'brand' ? 'var(--brand-700)' : 'var(--warning, #d97706)'
  const bg = tono === 'brand' ? 'var(--brand-50)' : 'rgba(217,119,6,0.10)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: bg, color, fontSize: 13, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFeatureSettings: '"tnum"',
      }}>
        {count}
      </div>
      <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
    </div>
  )
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th style={{
      textAlign: align ?? 'left', padding: '10px 14px',
      fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
      textTransform: 'uppercase', letterSpacing: 1.2,
    }}>
      {children}
    </th>
  )
}

function CreditoRowComp({ r }: { r: CreditoRow }) {
  const badge = ESTADO_BADGE[r.estado] ?? {
    label: r.estado, color: 'var(--ink-soft)', bg: 'rgba(75,85,99,0.08)',
  }
  return (
    <tr style={{
      borderBottom: '1px solid var(--line-soft)',
      transition: 'background 0.12s ease',
    }}
    onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg)' }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent' }}
    >
      <td style={{ padding: '12px 14px' }}>
        <Link to={`/neocore/creditos/${r.id}`}
          style={{ textDecoration: 'none', color: 'var(--ink)', fontWeight: 600 }}>
          {r.asociado_nombre}
        </Link>
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
          {r.asociado_no} · {r.id.slice(0, 8)}
        </div>
      </td>
      <td style={{ padding: '12px 14px', fontSize: 12 }}>{r.tipo_nombre}</td>
      <td style={{ padding: '12px 14px', textAlign: 'right', fontFeatureSettings: '"tnum"' }}>
        {fmtQ(r.capital_inicial)}
      </td>
      <td style={{ padding: '12px 14px', textAlign: 'right', fontFeatureSettings: '"tnum"', fontWeight: 700 }}>
        {fmtQ(r.saldo_capital)}
      </td>
      <td style={{ padding: '12px 14px', textAlign: 'right', fontFeatureSettings: '"tnum"' }}>
        {r.proxima_cuota_monto > 0 ? fmtQ(r.proxima_cuota_monto) : '—'}
      </td>
      <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-soft)' }}>
        {r.proxima_cuota_fecha ? fmtFecha(r.proxima_cuota_fecha) : fmtFecha(r.fecha_ultima_cuota)}
      </td>
      <td style={{ padding: '12px 14px' }}>
        {r.dias_atraso > 0 ? (
          <span style={{
            fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
            background: 'rgba(163,45,45,0.10)', color: 'var(--danger, #a32d2d)',
            textTransform: 'uppercase', letterSpacing: 0.5, fontFeatureSettings: '"tnum"',
          }}>
            {r.dias_atraso}d
          </span>
        ) : <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>—</span>}
      </td>
      <td style={{ padding: '12px 14px' }}>
        <span style={{
          fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
          background: badge.bg, color: badge.color,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {badge.label}
          {r.estado_provision && ['vigente', 'al_dia', 'en_mora'].includes(r.estado) && ` · ${r.estado_provision}`}
        </span>
      </td>
    </tr>
  )
}

// Receipt y Coins se referencian arriba en imports — TS warning placeholder
void Receipt
